import React from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { orange } from '@material-ui/core/colors'
import { Button as MuiButton, IconButton } from '@material-ui/core'
import { useSelector } from 'react-redux'
import { IRootReduxState } from 'types/reducers'
import { ButtonProps as MuiButtonProps } from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import AddIcon from '@material-ui/icons/Add'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import CancelIcon from '@material-ui/icons/Cancel'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteIcon from '@material-ui/icons/Delete'
import GroupIcon from '@material-ui/icons/Group'
import OpenWithIcon from '@material-ui/icons/OpenWith'
import PublishIcon from '@material-ui/icons/Publish'
import SkipNextIcon from '@material-ui/icons/SkipNext'
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit'
import SendIcon from '@material-ui/icons/Send'
import FilterListIcon from '@material-ui/icons/FilterList'
import PrintIcon from '@material-ui/icons/Print'
import SaveIcon from '@material-ui/icons/Save'
import { green } from '@material-ui/core/colors'

const useStyles = makeStyles((theme) => ({
  buttonIcon: {
    '& > svg': {
      marginRight: theme.spacing(1),
    },
  },
  buttonProgressSpinner: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  submitButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}))

export interface ButtonProps extends MuiButtonProps {
  disableOnClick?: boolean
  click?: (e: any) => void
  icon?: JSX.Element
  label?: string | JSX.Element
  minimal?: boolean
  fullscreen?: boolean
}

// Base button component — extracted so styled variants can wrap it at module level
const ButtonBase = (props: ButtonProps) => {
  const classes = useStyles()
  const [clicked, setClicked] = React.useState(false)
  const { disabled, disableOnClick, click, icon, label, variant, minimal, size, style: styleProp, ...theRestOfTheProps } = props
  if (minimal) {
    return (
      <IconButton
        {...theRestOfTheProps}
        size="small"
        color={props.color || 'primary'}
        disabled={disabled || (disableOnClick && clicked)}
        onClick={(e) => {
          setClicked(true)
          click && click(e)
        }}
      >
        {icon}
        {disableOnClick && clicked && <CircularProgress size={24} className={classes.buttonProgressSpinner} />}
      </IconButton>
    )
  }
  return (
    <MuiButton
      {...theRestOfTheProps}
      style={{ whiteSpace: 'nowrap', ...styleProp }}
      size={size ?? 'small'}
      disableElevation
      variant={variant || 'outlined'}
      color={props.color || 'primary'}
      disabled={disabled || (disableOnClick && clicked)}
      onClick={(e) => {
        setClicked(true)
        click && click(e)
      }}
      startIcon={icon}
    >
      <>{label}</>
      {disableOnClick && clicked && <CircularProgress size={24} className={classes.buttonProgressSpinner} />}
    </MuiButton>
  )
}

// Styled variants are defined ONCE at module scope so React sees a stable
// component type across re-renders. Building them inside the component body
// with `withStyles(...)` returns a brand-new component type on every render,
// which unmounts + remounts the underlying <button> element. If a parent
// re-renders between mousedown and click (e.g. an input blur that calls
// setState), the original button is gone before the click event fires and
// the form's type="submit" never triggers — user has to click twice.

// Shared dark-blue contained style (Create / Update / Save / Submit)
const PrimaryContainedBtnLight = withStyles({
  root: {
    backgroundColor: '#163A64 !important',
    color: '#fff !important',
    textTransform: 'none' as const,
    fontFamily: '"Public Sans", sans-serif',
    fontWeight: 500,
    borderRadius: '6px',
    padding: '8px 16px',
    boxShadow: 'none !important',
    border: '1px solid #163A64',
    '&:hover': {
      backgroundColor: '#123154 !important',
      borderColor: '#123154',
    },
    '&.Mui-disabled': {
      backgroundColor: '#163A64 !important',
      borderColor: '#163A64 !important',
      color: '#fff !important',
      opacity: 0.45,
    },
  },
})(ButtonBase)

const PrimaryContainedBtnDark = withStyles({
  root: {
    backgroundColor: '#1E3A5F !important',
    color: '#fff !important',
    textTransform: 'none' as const,
    fontFamily: '"Public Sans", sans-serif',
    fontWeight: 500,
    borderRadius: '6px',
    padding: '8px 16px',
    boxShadow: 'none !important',
    border: '1px solid #1E3A5F',
    '&:hover': {
      backgroundColor: '#2a5078 !important',
      borderColor: '#2a5078',
    },
    '&.Mui-disabled': {
      backgroundColor: '#1E3A5F !important',
      borderColor: '#1E3A5F !important',
      color: '#fff !important',
      opacity: 0.45,
    },
  },
})(ButtonBase)

const PrimaryContainedBtn = (props: ButtonProps) => {
  const isDarkMode = useSelector((state: IRootReduxState) => state.app.user.theme === 'dark')
  const StyledBtn = isDarkMode ? PrimaryContainedBtnDark : PrimaryContainedBtnLight
  return <StyledBtn {...props} />
}

// White outlined style (Cancel / Close)
const OutlinedCancelBtnLight = withStyles({
  root: {
    backgroundColor: '#fff',
    color: '#0F172A',
    textTransform: 'none' as const,
    fontFamily: '"Public Sans", sans-serif',
    fontWeight: 500,
    borderRadius: '6px',
    padding: '8px 16px',
    border: '1px solid #D6DDE7',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F8FAFC',
      borderColor: '#CBD5E1',
      boxShadow: 'none',
    },
  },
})(ButtonBase)

const OutlinedCancelBtnDark = withStyles({
  root: {
    backgroundColor: '#3a3a3a',
    color: '#E4E7EC',
    textTransform: 'none' as const,
    fontFamily: '"Public Sans", sans-serif',
    fontWeight: 500,
    borderRadius: '6px',
    padding: '8px 16px',
    border: '1px solid #555',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#4a4a4a',
      borderColor: '#777',
      boxShadow: 'none',
    },
  },
})(ButtonBase)

const OutlinedCancelBtn = (props: ButtonProps) => {
  const isDarkMode = useSelector((state: IRootReduxState) => state.app.user.theme === 'dark')
  const StyledBtn = isDarkMode ? OutlinedCancelBtnDark : OutlinedCancelBtnLight
  return <StyledBtn {...props} />
}

// Orange outlined danger style (Delete)
const DangerOutlinedBtnLight = withStyles({
  root: {
    color: orange[500],
    borderColor: orange[500],
    textTransform: 'none' as const,
    fontFamily: '"Public Sans", sans-serif',
    fontWeight: 500,
    borderRadius: '6px',
    padding: '8px 16px',
    boxShadow: 'none',
    '&:hover': {
      color: orange[700],
      borderColor: orange[700],
      boxShadow: 'none',
    },
  },
})(ButtonBase)

const DangerOutlinedBtnDark = withStyles({
  root: {
    color: '#fb923c',
    borderColor: '#fb923c',
    textTransform: 'none' as const,
    fontFamily: '"Public Sans", sans-serif',
    fontWeight: 500,
    borderRadius: '6px',
    padding: '8px 16px',
    boxShadow: 'none',
    '&:hover': {
      color: '#fdba74',
      borderColor: '#fdba74',
      boxShadow: 'none',
    },
  },
})(ButtonBase)

const DangerOutlinedBtn = (props: ButtonProps) => {
  const isDarkMode = useSelector((state: IRootReduxState) => state.app.user.theme === 'dark')
  const StyledBtn = isDarkMode ? DangerOutlinedBtnDark : DangerOutlinedBtnLight
  return <StyledBtn {...props} />
}

const Button = {
  Button: ButtonBase,
  SubmitButton: (props: ButtonProps) => {
    const { label, icon, className } = props
    const classes = useStyles()
    return (
      <PrimaryContainedBtn
        {...props}
        variant="contained"
        className={className ?? classes.submitButton}
        type="submit"
        icon={icon || <PublishIcon />}
        label={label || <FormattedMessage id="submit" defaultMessage="Submit" />}
      />
    )
  },
  SubmitButtonJourneyManagement: (props: ButtonProps) => {
    const { label, icon } = props
    const classes = useStyles()
    return <ButtonBase {...props} className={classes.submitButton} type="submit" label={label || <FormattedMessage id="submit" defaultMessage="Submit" />} />
  },
  SaveButton: (props: ButtonProps) => {
    const { label, icon } = props
    const classes = useStyles()
    return (
      <PrimaryContainedBtn
        {...props}
        variant="contained"
        className={classes.submitButton}
        type="button"
        icon={icon || <SaveIcon />}
        label={label || <FormattedMessage id="save" defaultMessage="Save" />}
      />
    )
  },
  UploadButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <PrimaryContainedBtn {...props} variant="contained" icon={icon || <CloudUploadIcon />} label={label || <FormattedMessage id="upload" defaultMessage="Upload" />} />
  },
  DeleteButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <DangerOutlinedBtn {...props} icon={icon || <DeleteIcon />} label={label || <FormattedMessage id="delete" defaultMessage="Delete" />} />
  },
  EditButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <PrimaryContainedBtn {...props} variant="contained" icon={icon || <EditOutlinedIcon />} label={label || <FormattedMessage id="edit" defaultMessage="Edit" />} />
  },
  FilterButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <OutlinedCancelBtn {...props} variant="outlined" icon={icon || <FilterListIcon />} label={label || <FormattedMessage id="filter" defaultMessage="Filter" />} />
  },
  DetailsButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <OutlinedCancelBtn {...props} variant="outlined" icon={icon || <EditOutlinedIcon />} label={label || <FormattedMessage id="details" defaultMessage="Details" />} />
  },
  DownloadButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <PrimaryContainedBtn {...props} variant="contained" icon={icon || <CloudDownloadIcon />} label={label || <FormattedMessage id="download" defaultMessage="Download" />} />
  },
  UpdateButton: (props: ButtonProps) => {
    const { label, icon } = props
    return (
      <PrimaryContainedBtn
        {...props}
        variant="contained"
        icon={icon || <EditOutlinedIcon />}
        label={label || <FormattedMessage id="update" defaultMessage="Update" />}
      />
    )
  },
  CreateButton: (props: ButtonProps) => {
    const { label, icon } = props
    return (
      <PrimaryContainedBtn
        {...props}
        variant="contained"
        icon={icon || <AddIcon />}
        label={label || <FormattedMessage id="new" defaultMessage="New" />}
      />
    )
  },
  MembersButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <ButtonBase {...props} icon={icon || <GroupIcon />} label={label || <FormattedMessage id="members" defaultMessage="Members" />} />
  },
  MoveButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <ButtonBase {...props} icon={icon || <OpenWithIcon />} label={label || <FormattedMessage id="move" defaultMessage="Move" />} />
  },
  YesButton: (props: ButtonProps) => {
    const { label, icon } = props
    return (
      <PrimaryContainedBtn
        {...props}
        variant="contained"
        icon={icon || <CheckIcon />}
        label={label || <FormattedMessage id="yes" defaultMessage="Yes" />}
      />
    )
  },
  OkButton: (props: ButtonProps) => {
    const { label, icon } = props
    return (
      <PrimaryContainedBtn
        {...props}
        variant="contained"
        icon={icon || <CheckIcon />}
        label={label || <FormattedMessage id="ok" defaultMessage="OK" />}
      />
    )
  },
  CancelButton: (props: ButtonProps) => {
    const { label, icon } = props
    return (
      <OutlinedCancelBtn
        {...props}
        variant="outlined"
        icon={icon}
        label={label || <FormattedMessage id="close" defaultMessage="Close" />}
      />
    )
  },
  CloseButton: (props: ButtonProps) => {
    const { icon, click } = props
    return (
      <IconButton onClick={click} aria-label="delete" size="small">
        {icon || <CloseIcon />}
      </IconButton>
    )
  },
  NoButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <OutlinedCancelBtn {...props} variant="outlined" icon={icon || <CancelIcon />} label={label || <FormattedMessage id="no" defaultMessage="No" />} />
  },
  NextButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <ButtonBase {...props} color="secondary" icon={icon || <SkipNextIcon />} label={label} />
  },
  PreviousButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <ButtonBase {...props} color="secondary" icon={icon || <SkipPreviousIcon />} label={label} />
  },
  FullscreenButton: (props: ButtonProps) => {
    const { label, icon, fullscreen } = props
    return <ButtonBase {...props} color="secondary" icon={icon || fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />} label={label} />
  },
  NotificationButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <ButtonBase {...props} color="secondary" icon={icon || <SendIcon />} label={label} />
  },
  PrintButton: (props: ButtonProps) => {
    const { label, icon } = props
    return <ButtonBase {...props} color="secondary" icon={icon || <PrintIcon />} label={label} />
  },
}

export default Button
